import React, { useCallback, useEffect, useState } from "react";
import { Ear, Radio, Users, RefreshCw, PhoneCall, Loader2 } from "lucide-react";
import api from "../lib/api";
import { supervise } from "../lib/softphone";

const since = (iso) => {
  const t = new Date(iso).getTime();
  if (isNaN(t)) return "";
  const s = Math.max(0, Math.round((Date.now() - t) / 1000));
  return `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
};

/**
 * Supervisor panel: who is on a call right now, and the three ways to attach.
 * Admins and team leaders only - the API refuses everyone else regardless.
 */
export default function LiveCalls() {
  const [calls, setCalls] = useState([]);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState("");
  const [loading, setLoading] = useState(true);
  const [, tick] = useState(0);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/telephony/active");
      setCalls(data.calls || []);
      setErr("");
    } catch (e) {
      setErr(e.response?.data?.detail || "Could not load live calls.");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); const i = setInterval(load, 5000); return () => clearInterval(i); }, [load]);
  useEffect(() => { const i = setInterval(() => tick((n) => n + 1), 1000); return () => clearInterval(i); }, []);

  const attach = async (c, mode) => {
    setBusy(c.id + mode); setErr("");
    try { await supervise(c.id, mode); }
    catch (e) { setErr(e.message); }
    finally { setBusy(""); }
  };

  const Btn = ({ c, mode, icon: Icon, label, tone }) => (
    <button onClick={() => attach(c, mode)} disabled={!!busy} data-testid={`sup-${mode}-${c.id}`}
      title={label}
      className={`px-2.5 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 disabled:opacity-50 ${tone}`}>
      {busy === c.id + mode ? <Loader2 size={13} className="animate-spin" /> : <Icon size={13} />}
      <span className="hidden sm:inline">{label}</span>
    </button>
  );

  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900" data-testid="live-calls">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-200 dark:border-slate-800">
        <PhoneCall size={16} className="text-emerald-600 dark:text-emerald-400" />
        <h3 className="font-semibold text-slate-900 dark:text-white text-sm flex-1">Live calls</h3>
        <span className="text-xs text-slate-500 dark:text-slate-400">{calls.length} active</span>
        <button onClick={load} aria-label="Refresh" className="text-slate-400 hover:text-slate-700 dark:hover:text-white">
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
        </button>
      </div>

      {err && <p className="px-4 py-2 text-xs text-rose-600 dark:text-rose-400" role="alert">{err}</p>}

      {!calls.length ? (
        <p className="px-4 py-8 text-center text-sm text-slate-500 dark:text-slate-400">
          {loading ? "Loading…" : "No one is on a call right now."}
        </p>
      ) : (
        <div className="divide-y divide-slate-100 dark:divide-slate-800">
          {calls.map((c) => (
            <div key={c.id} className="px-4 py-3 flex items-center gap-3 flex-wrap">
              <span className={`h-2 w-2 rounded-full shrink-0 ${c.status === "answered" ? "bg-emerald-500" : "bg-amber-400 animate-pulse"}`} />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-900 dark:text-white truncate">
                  {c.agent_name} <span className="text-slate-400 font-normal">→</span> {c.client_name || c.to_number}
                </p>
                <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
                  {c.to_number}{c.agent_extension ? ` · ext ${c.agent_extension}` : ""} · {c.status} · {since(c.started_at)}
                </p>
              </div>
              <div className="flex items-center gap-1.5">
                <Btn c={c} mode="listen" icon={Ear} label="Listen"
                  tone="bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-700" />
                <Btn c={c} mode="whisper" icon={Radio} label="Whisper"
                  tone="bg-amber-100 dark:bg-amber-500/20 text-amber-800 dark:text-amber-300 hover:bg-amber-200" />
                <Btn c={c} mode="barge" icon={Users} label="Join"
                  tone="bg-indigo-600 text-white hover:bg-indigo-700" />
              </div>
            </div>
          ))}
        </div>
      )}
      <p className="px-4 py-2 text-[11px] text-slate-400 border-t border-slate-100 dark:border-slate-800">
        Listen is one-way; Whisper is heard only by the agent; Join puts you in the conversation.
      </p>
    </div>
  );
}
